export const DEVELOPERS = [
    {
        name: 'Sandesh Athni Hiremath',
        role: 'Platform Architect',
        bio: 'Builds EvolveX — sims, planners, and arcade agents.',
        email: 'sandesh.hiremath@rptu.de',
    },
];

export const DOC_CATEGORIES = ['Biological', 'Arcade', 'Robotic', 'Art'];

export const docsPosts = [
    {
        id: 'bio-turing',
        category: 'Biological',
        title: 'AI Turing Patterns',
        author: 'Evolving Systems Lab',
        datePublished: '2026-01-10',
        numComments: 4,
        description:
            'Reaction-diffusion systems generate stationary and oscillatory Turing patterns. The activator-inhibitor form $\\frac{\\partial u}{\\partial t} = D_u \\nabla^2 u + f(u,v)$ provides a compact framework for morphogenesis.',
        content:
            'Turing instability occurs when diffusion destabilizes a homogeneous steady state. For activator-inhibitor kinetics, short-range activation and long-range inhibition yield spots, stripes, and labyrinthine modes. EvolveX exposes transform and generate APIs for exploring parameter sweeps.',
        equations: [
            '\\frac{\\partial u}{\\partial t} = D_u \\nabla^2 u + f(u,v), \\quad \\frac{\\partial v}{\\partial t} = D_v \\nabla^2 v + g(u,v)',
        ],
    },
    {
        id: 'bio-lotka',
        category: 'Biological',
        title: 'Lotka-Volterra Dynamics',
        author: 'Evolving Systems Lab',
        datePublished: '2026-01-12',
        numComments: 2,
        description:
            'Coupled ODEs $\\frac{dx}{dt} = \\alpha x - \\beta x y$ and $\\frac{dy}{dt} = \\delta x y - \\gamma y$ model predator-prey cycles with closed orbits in the classical regime. EvolveX also ships 2D diffusive and stochastic SDE variants.',
        content:
            'Phase-plane analysis reveals neutrally stable orbits when parameters satisfy certain balances. Perturbations trace limit cycles in extended models with logistic prey growth or Holling-type functional responses. The 2D spatial variant couples local oscillators via diffusion to produce traveling population waves, while the SDE variant adds multiplicative noise $dx = (\\alpha x - \\beta xy)\\,dt + \\sigma_1 x\\,dW_1$ to broaden orbits and model environmental fluctuations.',
        equations: [
            '\\frac{dx}{dt} = \\alpha x - \\beta x y, \\qquad \\frac{dy}{dt} = \\delta x y - \\gamma y',
            '\\frac{\\partial x}{\\partial t} = \\alpha x - \\beta x y + D\\nabla^2 x',
        ],
    },
    {
        id: 'bio-fisher',
        category: 'Biological',
        title: 'Fisher-KPP Propagation',
        author: 'Evolving Systems Lab',
        datePublished: '2026-01-14',
        numComments: 1,
        description:
            'The Fisher equation $\\frac{\\partial u}{\\partial t} = D\\nabla^2 u + r u(1-u)$ admits traveling-wave solutions with minimal speed $c_{\\min} = 2\\sqrt{Dr}$. EvolveX visualizes 1D fronts, 2D expanding colonization rings, and noise-roughened stochastic interfaces.',
        content:
            'Front propagation speed depends on diffusion and growth rate. Numerical schemes must balance stability (CFL-like constraints) with sharp interface resolution. In 2D, a localized seed expands into a radially symmetric front with mesmerizing texture; adding multiplicative noise produces a roughened, fractal-like interface reminiscent of biological colonization fronts.',
        equations: [
            '\\frac{\\partial u}{\\partial t} = D\\nabla^2 u + r u (1-u), \\qquad c_{\\min} = 2\\sqrt{Dr}',
        ],
    },
    {
        id: 'bio-fhn',
        category: 'Biological',
        title: 'FitzHugh-Nagumo Excitability',
        author: 'Evolving Systems Lab',
        datePublished: '2026-01-16',
        numComments: 3,
        description:
            'A two-variable reduction of Hodgkin-Huxley dynamics captures threshold spikes via fast voltage v and slow recovery w. EvolveX also renders a 2D excitable medium producing rotating spiral waves and a noise-driven coherence-resonance variant.',
        content:
            'Nullcline intersections classify rest, oscillatory, and excitable regimes. Stimulus I shifts the v-nullcline and can trigger a single action-potential-like trajectory. Diffusively coupling a grid of FitzHugh-Nagumo cells yields spiral waves analogous to cardiac re-entry; adding noise below threshold can spontaneously trigger spikes (coherence resonance).',
        equations: [
            '\\frac{dv}{dt} = v - \\frac{v^3}{3} - w + I, \\qquad \\frac{dw}{dt} = \\frac{v + a - b w}{\\tau}',
        ],
    },
    {
        id: 'bio-hh',
        category: 'Biological',
        title: 'Hodgkin-Huxley Ion Channels',
        author: 'Evolving Systems Lab',
        datePublished: '2026-01-18',
        numComments: 5,
        description:
            'Membrane voltage follows $C_m \\frac{dV}{dt} = I - g_{Na} m^3 h (V - E_{Na}) - g_K n^4 (V - E_K) - g_L (V - E_L)$ with gating variables $m$, $h$, $n$ driven by voltage-dependent rates. EvolveX shows single spikes, sustained spike trains, and channel-noise-driven irregular firing.',
        content:
            'Sodium activation produces rapid depolarization; potassium repolarizes the membrane. Refractory periods emerge from coupled $m$, $h$, and $n$ kinetics. Brief threshold pulses trigger a single all-or-none spike, sustained current drives periodic spike trains with current-dependent firing rate, and injected channel noise produces the irregular inter-spike intervals seen in real neurons.',
        equations: [
            'C_m \\frac{dV}{dt} = I - g_{Na} m^3 h (V - E_{Na}) - g_K n^4 (V - E_K) - g_L (V - E_L)',
        ],
    },
    {
        id: 'bio-segregation',
        category: 'Biological',
        title: 'Schelling Segregation',
        author: 'Evolving Systems Lab',
        datePublished: '2026-03-01',
        numComments: 3,
        description:
            'Agents relocate when local same-group fraction falls below $\\tau$, producing emergent segregation from weak individual preferences on a spatial lattice.',
        content:
            'Schelling showed that even tolerant agents who prefer modest homogeneity can sort into sharply divided regions. EvolveX runs the model as an interactive agent-based simulation with live cluster metrics.',
        equations: ['U_i = \\mathbb{1}\\left[\\frac{\\text{same}_i}{\\text{occupied}_i} \\geq \\tau\\right]'],
    },
    {
        id: 'bio-epidemic',
        category: 'Biological',
        title: 'Virus Spread (SIR)',
        author: 'Evolving Systems Lab',
        datePublished: '2026-03-03',
        numComments: 5,
        description:
            'Susceptible-Infected-Recovered agents on a grid spread infection locally with rates $\\beta$ and recover with rate $\\gamma$.',
        content:
            'Lattice SIR models reveal traveling infection fronts and spatial heterogeneity absent from well-mixed compartment ODEs. EvolveX couples the spatial grid with compartment curves to compare local and mean-field dynamics.',
        equations: [
            '\\frac{dS}{dt} = -\\beta SI, \\quad \\frac{dI}{dt} = \\beta SI - \\gamma I, \\quad \\frac{dR}{dt} = \\gamma I',
        ],
    },
    {
        id: 'bio-flocking',
        category: 'Biological',
        title: 'Flocking (Boids)',
        author: 'Evolving Systems Lab',
        datePublished: '2026-03-04',
        numComments: 6,
        description:
            'Boids follow separation, alignment, and cohesion rules to produce coherent swarm motion from local interactions alone.',
        content:
            'Reynolds flocking demonstrates decentralized coordination from three local rules. EvolveX extends the baseline swarm with predator pursuit, producing rich pursuit-escape dynamics atop collective motion.',
        equations: ['\\mathbf{v}_{new} = \\mathbf{v} + w_s \\mathbf{S} + w_a \\mathbf{A} + w_c \\mathbf{C}'],
    },
    {
        id: 'robot-markov',
        category: 'Robotic',
        title: 'Markov Chains for Mobility',
        author: 'Robotic Systems Lab',
        datePublished: '2026-02-01',
        numComments: 2,
        description:
            'Discrete-state Markov chains model transitions P(X_{t+1}=j | X_t=i) = p_ij. Stationary distributions satisfy pi = pi * P.',
        content:
            'Graph Builder visualizes state spaces and transition edges. Ergodic chains converge to unique stationary distributions; absorbing states model terminal configurations.',
    },
    {
        id: 'robot-mobility',
        category: 'Robotic',
        title: 'Mobility Systems and MAPF',
        author: 'Robotic Systems Lab',
        datePublished: '2026-02-03',
        numComments: 6,
        description:
            'Multi-agent path finding assigns collision-free routes on grids. Conflict-based search and prioritized planning are common algorithmic families.',
        content:
            'Mobility Systems (Grid Editor) encodes start/goal pairs and obstacles. MAPF solvers return joint plans minimizing sum-of-costs or makespan subject to kinematic constraints.',
    },
    {
        id: 'robot-eoq',
        category: 'Robotic',
        title: 'Economic Order Quantity (EOQ)',
        author: 'Robotic Systems Lab',
        datePublished: '2026-07-20',
        numComments: 2,
        description:
            'EOQ balances fixed ordering cost $K$ against holding cost $h$ under steady demand $D$, with closed-form optimum $Q^*=\\sqrt{2DK/h}$.',
        content:
            'Total cost $TC(Q)=\\frac{D}{Q}K+\\frac{Q}{2}h$ is convex for $Q>0$. Setting the derivative to zero yields the classic square-root lot size. EvolveX exposes EOQ in the Supply Chain tab of the Graph Editor for instant analytical solves across SKUs.',
        equations: [
            'TC(Q) = \\frac{D}{Q}K + \\frac{Q}{2}h, \\qquad Q^* = \\sqrt{\\frac{2DK}{h}}',
        ],
    },
    {
        id: 'robot-transshipment',
        category: 'Robotic',
        title: 'Transshipment Networks',
        author: 'Robotic Systems Lab',
        datePublished: '2026-07-21',
        numComments: 3,
        description:
            'Goods move from plants $I$ through DCs $J$ to customers $K$ under supply, flow conservation, and demand constraints at minimum shipping cost.',
        content:
            'Decision variables $x_{ij}$ and $y_{jk}$ are continuous shipments. The LP enforces plant capacities, DC balance $\\sum_i x_{ij}=\\sum_k y_{jk}$, and exact demand fulfillment. The Graph Editor network canvas lets users place plants, DCs, and customers; edge labels supply unit costs $c_{ij}$, $f_{jk}$.',
        equations: [
            '\\min \\sum_{i\\in I}\\sum_{j\\in J} c_{ij}x_{ij} + \\sum_{j\\in J}\\sum_{k\\in K} f_{jk}y_{jk}',
            '\\sum_{j} x_{ij} \\le S_i,\\quad \\sum_{i} x_{ij} = \\sum_{k} y_{jk},\\quad \\sum_{j} y_{jk} = d_k',
        ],
    },
    {
        id: 'robot-facility',
        category: 'Robotic',
        title: 'Facility Location-Allocation',
        author: 'Robotic Systems Lab',
        datePublished: '2026-07-22',
        numComments: 2,
        description:
            'Binary opens $y_i$ select warehouses; continuous $x_{ij}$ allocate demand fractions under capacity $M_i y_i$ while minimizing fixed plus transport cost.',
        content:
            'The uncapacitated and capacitated facility-location models are NP-hard MILPs. EvolveX enumerates feasible open sets for small instances and solves the allocation LP for each candidate, returning opened sites and customer assignments. Linking constraints $x_{ij}\\le y_i$ prevent serving from closed facilities.',
        equations: [
            '\\min \\sum_{i\\in I} f_i y_i + \\sum_{i\\in I}\\sum_{j\\in J} c_{ij} d_j x_{ij}',
            '\\sum_{i} x_{ij} = 1,\\quad \\sum_{j} d_j x_{ij} \\le M_i y_i,\\quad x_{ij}\\le y_i',
        ],
    },
    {
        id: 'robot-cvrp',
        category: 'Robotic',
        title: 'Capacitated Vehicle Routing (CVRP)',
        author: 'Robotic Systems Lab',
        datePublished: '2026-07-23',
        numComments: 4,
        description:
            'Identical vehicles of capacity $Q$ leave a depot, visit each customer once, and return, minimizing total travel $\\sum c_{ij}x_{ijk}$.',
        content:
            'CVRP couples a TSP-like tour structure with knapsack capacity per vehicle. Exact MIP formulations grow quickly; EvolveX uses a constructive nearest-neighbor packing heuristic with 2-opt polishing, suitable for interactive demos. Subtour elimination and fleet sizing emerge implicitly from the constructive routes.',
        equations: [
            '\\min \\sum_{k\\in K}\\sum_{i\\in V}\\sum_{j\\in V} c_{ij} x_{ijk}',
            '\\sum_{k,i} x_{ijk}=1\\ (j\\ne 0),\\quad \\sum_{i} d_i\\sum_{j} x_{ijk}\\le Q',
        ],
    },
    {
        id: 'robot-sde-routing',
        category: 'Robotic',
        title: 'SDE-Constrained Inventory Routing',
        author: 'Robotic Systems Lab',
        datePublished: '2026-07-24',
        numComments: 3,
        description:
            'Store inventories follow Itô SDEs $dI_i=(-\\mu_i+u_i)dt+\\sigma_i dW_i$ while a dispatch control $v(t)$ and replenishment $u_i(t)$ trade holding, stockout, and transport cost.',
        content:
            'Stochastic demand couples continuous inventory dynamics with discrete vehicle dispatches. EvolveX simulates Euler–Maruyama paths, applies a threshold MPC-style policy that dispatches when expected inventory would breach $I_{\\min}$, and reports Monte Carlo expected cost plus mean inventory trajectories in the Graph Editor Supply Chain tab.',
        equations: [
            'dI_i(t) = (-\\mu_i(t)+u_i(t))\\,dt + \\sigma_i(t)\\,dW_i(t)',
            '\\min_{u,v}\\ \\mathbb{E}\\Big[\\int_0^T \\big(\\sum_i (h_i[I_i]_+ + p_i[-I_i]_+) + c\\,v(t)\\big)dt\\Big]',
            '\\sum_i u_i(t) \\le W\\,v(t),\\qquad \\mathbb{E}[I_i(t)] \\ge I_{\\min}',
        ],
    },
    {
        id: 'art-audio',
        category: 'Art',
        title: 'Audio-Visualizer',
        author: 'Creative Systems Lab',
        datePublished: '2026-02-05',
        numComments: 1,
        description:
            'Short-time Fourier transforms map audio s(t) to spectrogram S(f,t). Peak frequencies drive generative visuals in real time.',
        content:
            'Windowed FFT with hop size H yields time-frequency tiles. RMS energy and spectral centroid provide compact features for reactive visual shaders.',
    },
    {
        id: 'arcade-snake',
        category: 'Arcade',
        title: 'Snake: Rules and Greedy Agents',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-10',
        numComments: 8,
        description:
            'Rules: grow by eating food, avoid walls and self-collision. Greedy agents use Manhattan distance to food with wall-avoidance heuristics.',
        content:
            'Hamiltonian cycle strategies guarantee safety on bounded grids at the cost of efficiency. A* to food with tail-reachable fallback is a strong baseline for agent uploads.',
    },
    {
        id: 'arcade-sliding',
        category: 'Arcade',
        title: 'Sliding Tiles: IDA* and Parity',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-11',
        numComments: 4,
        description:
            'Rules: slide tiles into the blank to reach the goal permutation. Solvable instances satisfy parity constraints on inversions plus blank-row index.',
        content:
            'IDA* with Manhattan heuristic is the standard optimal solver. Pattern databases accelerate lookup for 15-puzzle benchmarks.',
    },
    {
        id: 'arcade-tictactoe',
        category: 'Arcade',
        title: 'Tic-Tac-Toe: Minimax',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-12',
        numComments: 3,
        description:
            'Rules: players alternate placing marks on a 3x3 grid; three in a row wins. Perfect play with minimax always draws.',
        content:
            'Minimax with alpha-beta pruning explores the full game tree instantly. Heuristic agents can prioritize center and corner openings.',
    },
    {
        id: 'arcade-pong',
        category: 'Arcade',
        title: 'Pong: Tracking and Prediction',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-13',
        numComments: 2,
        description:
            'Rules: paddle intercepts ball; miss costs a point. Agents predict y-intercept from ball velocity for paddle positioning.',
        content:
            'Linear extrapolation with wall-bounce reflection handles basic tracking. Noise-aware filters help when ball speed varies between rallies.',
    },
    {
        id: 'arcade-connect4',
        category: 'Arcade',
        title: 'Connect Four: Threat Search',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-14',
        numComments: 5,
        description:
            'Rules: drop tokens into a 7x6 grid; four aligned wins. Column moves require gravity simulation.',
        content:
            'Threat-based search detects immediate wins and blocks. Depth-limited minimax with column ordering heuristics is effective for agent baselines.',
    },
    {
        id: 'arcade-pole',
        category: 'Arcade',
        title: 'Two-Joint Pole: LQR Control',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-15',
        numComments: 2,
        description:
            'Rules: apply cart force to balance coupled pendulums. Linearized dynamics around upright equilibrium enable LQR controllers.',
        content:
            'State vector includes cart position, velocities, and joint angles. Reward agents maximize upright duration under force limits.',
    },
    {
        id: 'arcade-bandit',
        category: 'Arcade',
        title: 'One-Armed Bandit: Epsilon-Greedy',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-16',
        numComments: 1,
        description:
            'Rules: pull lever for stochastic rewards. Epsilon-greedy and UCB balance exploration and exploitation.',
        content:
            'Empirical mean rewards converge to true payout rates. Thompson sampling is a Bayesian alternative for multi-armed bandits.',
    },
    {
        id: 'arcade-blackjack',
        category: 'Arcade',
        title: 'Blackjack: Basic Strategy',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-17',
        numComments: 3,
        description:
            'Rules: approach 21 without busting; dealer hits on soft 17. Basic strategy tables minimize house edge.',
        content:
            'Card counting adjusts bet sizing when high cards remain. Agent uploads implement hit/stand/double policies from strategy matrices.',
    },
    {
        id: 'arcade-lights',
        category: 'Arcade',
        title: 'Lights Out: Gaussian Elimination',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-18',
        numComments: 2,
        description:
            'Rules: toggling a cell flips its neighbors. Press-vector solutions live in GF(2) and can be found via linear algebra.',
        content:
            'Toggling matrix over GF(2) determines solvability. Optimal agents minimize number of presses for all-off goal.',
    },
    {
        id: 'arcade-battleship',
        category: 'Arcade',
        title: 'Battleship: Hunt and Target',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-19',
        numComments: 4,
        description:
            'Rules: fire at hidden ships; hits reveal occupied cells. Hunt-target mode alternates random search with line completion.',
        content:
            'Probability density maps over remaining ship placements guide next shots. Parity checks reduce search space on large boards.',
    },
    {
        id: 'arcade-numberlink',
        category: 'Arcade',
        title: 'Numberlink: Path Packing',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-20',
        numComments: 2,
        description:
            'Rules: connect matching numbers with non-crossing paths covering the board. Backtracking with MRV ordering solves small instances.',
        content:
            'Agents maintain occupancy grids and extend paths greedily toward targets while preserving connectivity for remaining pairs.',
    },
    {
        id: 'arcade-minesweeper',
        category: 'Arcade',
        title: 'Minesweeper: Constraint Propagation',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-21',
        numComments: 6,
        description:
            'Rules: reveal safe cells; numbers count adjacent mines. Constraint propagation deduces forced flags and opens.',
        content:
            'When deduction stalls, agents sample lowest-risk frontier cells. CSP solvers handle local mine configurations exactly.',
    },
    {
        id: 'arcade-road',
        category: 'Arcade',
        title: 'Road Fighter: Lane MDP',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-22',
        numComments: 1,
        description:
            'Rules: dodge traffic and reach distance goals. Lane-change MDPs model obstacle gaps over short horizons.',
        content:
            'Look-ahead evaluates collision risk per lane. Q-learning on discretized state buckets trains reactive driving agents.',
    },
    {
        id: 'arcade-pacman',
        category: 'Arcade',
        title: 'Pac-Man: Ghost Avoidance',
        author: 'Arcade Systems Lab',
        datePublished: '2026-02-23',
        numComments: 7,
        description:
            'Rules: eat pellets, avoid ghosts, power pellets enable ghost reversal. A* to nearest pellet with ghost-distance penalty is a strong heuristic.',
        content:
            'BFS on grid graphs finds shortest pellet tours locally. Flee modes trigger when ghost Manhattan distance drops below threshold.',
    },
];

export const docsAuthors = {
    'Evolving Systems Lab': { firstName: 'Evolving Systems Lab' },
    'Robotic Systems Lab': { firstName: 'Robotic Systems Lab' },
    'Creative Systems Lab': { firstName: 'Creative Systems Lab' },
    'Arcade Systems Lab': { firstName: 'Arcade Systems Lab' },
};
